import {PracticeTestFeedbackData} from 'store/reducers/questionnaires';
import getSaebReadingLevel, {
  SAEB_READING_LEVELS,
  getSaebReadingLevelByCorrectCount,
  getSaebReadingLevelDescriptionByCorrectCount,
} from 'utils/functions/getSaebReadingLevel';

export interface ResolvedSaebLevel {
  title: string;
  emoji: string;
  description: string;
  levelIndex: number;
  source: 'backend' | 'computed';
  // Fatia 5: number of correct answers and the test's question count. Present only on the Reading
  // Cycle branch (source: 'backend'); undefined on the legacy SAEB simulado, which keeps showing
  // the 0-500 score. The container uses these to render the "x de N" indicator (R2) and to hide
  // the Nota/Tempo cards.
  correctCount?: number;
  questionCount?: number;
}

// Confirmed against the real diff of letrustech/letrus-api#2213: level_title is the student's
// title (Iniciante/Aprendiz/Em Prática/Confiante), null outside the Reading Cycle trails —
// coexists with level_label (pedagogical SAEB name, used for trail routing,
// "Inicial"/"Básico"/"Proficiente"/"Avançado", must not be shown to the student). The presence
// of level_title (non-null) is the correct signal for which scale came back.

// Code review finding (2026-09-03): this screen is generic (/atividades/simulado/:id/resultado),
// used by any SAEB simulado, not just the Reading Cycle. 26 questions (GA-05) is only real when
// the backend confirms the Reading Cycle via level_title; outside it, keep the legacy SAEB
// simulado's 30 questions (the value this screen already showed before this feature) instead of
// inheriting the wrong number.
const READING_CYCLE_QUESTION_COUNT = 26;
const LEGACY_SAEB_QUESTION_COUNT = 30;

export default function resolveSaebFeedbackLevel(
  feedback: PracticeTestFeedbackData,
  // True on the diagnostic (SAEB 1): the description can tease "sua trilha vai te ajudar a
  // avançar". False on the final/comparison activity (SAEB 2) — the trilha already happened by
  // then, so the same teaser wrongly implies another activity is still coming.
  mentionsUpcomingTrail: boolean = true,
): ResolvedSaebLevel {
  const computed = getSaebReadingLevel(feedback.score ?? 0);

  if (feedback.level_title) {
    // Real acertos from the backend (CPAL-SAEB, confirmed live 2026-09-18) — closes the
    // TODO(fatia3) that used to fall back to a mocked count.
    const correctCount = feedback.correct_answers ?? 0;

    // The backend's explicit `level` (1-4) is the source of truth for the band when present, so
    // title / level / emoji / description stay CONSISTENT with each other. The correct-count band
    // is only the fallback when the backend omits `level` (Fatia 5: on the Reading Cycle the level
    // then comes from the count, not the score). This avoids a title×emoji mismatch (e.g. backend
    // "Aprendiz" with a count that lands on a different band's emoji).
    const questionCount =
      feedback.total_questions ?? READING_CYCLE_QUESTION_COUNT;
    const bandByCount = getSaebReadingLevelByCorrectCount(correctCount);
    const levelIndex = feedback.level ?? bandByCount.levelIndex;
    const band = SAEB_READING_LEVELS[levelIndex - 1] ?? bandByCount;

    return {
      // The emoji is ours alone (doesn't exist on the backend). The "Leitor " prefix was
      // documented as a front-end-only copy decision (letrus-api#2213 diff at the time), but a
      // live report (2026-09-23) showed the backend sometimes already sends the full title with
      // "Leitor" included (e.g. "Leitor iniciante"), producing "Leitor Leitor iniciante" on
      // screen. Prepending only when the backend value doesn't already start with "Leitor" keeps
      // both contracts working without needing a synchronized backend fix first.
      title: /^leitor\b/i.test(feedback.level_title)
        ? feedback.level_title
        : `Leitor ${feedback.level_title}`,
      emoji: band.emoji,
      // Fatia 5 R3: nota-free description (score removed from the student's screen). Uses the
      // resolved band index (1-4) to pick the copy. Placeholder copy — final wording is [GATE-PM].
      description: getSaebReadingLevelDescriptionByCorrectCount(
        band.levelIndex,
        correctCount,
        questionCount,
        mentionsUpcomingTrail,
      ),
      levelIndex,
      source: 'backend',
      correctCount,
      questionCount,
    };
  }

  return {
    title: computed.title,
    emoji: computed.emoji,
    description: computed.getDescription(
      feedback.score ?? 0,
      LEGACY_SAEB_QUESTION_COUNT,
    ),
    levelIndex: computed.levelIndex,
    source: 'computed',
  };
}
